
/**
 * ResultsHeader.tsx
 *
 * ResultsHeader component for the Marketplace UI.
 * Displays the results header, applied search, filter chips, and clear/back controls.
 *
 * Features:
 * - Shows results count, applied search, and filter chips
 * - Allows clearing all filters or going back
 * - Responsive and accessible UI
 *
 * @module ResultsHeader
 */

import React, { useState, useEffect } from 'react';
import { Icon } from '@ellucian/ds-icons/lib';
import { Typography, Chip, Badge } from '@ellucian/react-design-system/core';

/**
 * Props for the ResultsHeader component.
 * @property appliedSearch - The applied search string (if any)
 * @property filteredSolutionsCount - Number of filtered solutions
 * @property selectedCategories - Currently selected categories
 * @property setSelectedCategories - Callback to update selected categories
 * @property onClearSearch - Callback to clear the search
 * @property showCreatedByFilter - Whether to show author filter chips
 * @property selectedAuthors - Currently selected authors
 * @property setSelectedAuthors - Callback to update selected authors
 * @property selectedTypes - Currently selected types
 * @property setSelectedTypes - Callback to update selected types
 * @property selectedIntegratedSolutions - Currently selected integrated solutions
 * @property setSelectedIntegratedSolutions - Callback to update selected integrated solutions
 * @property showOnlyEntitledSolutions - Whether to filter by entitled solutions
 * @property setShowOnlyEntitledSolutions - Callback to update entitled filter
 * @property onClearAll - Callback to clear all filters
 */
interface ResultsHeaderProps {
  appliedSearch?: string;
  filteredSolutionsCount: number;
  selectedCategories?: string[];
  setSelectedCategories?: (categories: string[]) => void;
  onClearSearch?: () => void;
  showCreatedByFilter?: boolean;
  selectedAuthors?: string[];
  setSelectedAuthors?: (authors: string[]) => void;
  selectedTypes?: string[];
  setSelectedTypes?: (types: string[]) => void;
  selectedIntegratedSolutions?: string[];
  setSelectedIntegratedSolutions?: (solutions: string[]) => void;
  showOnlyEntitledSolutions?: boolean;
  setShowOnlyEntitledSolutions?: (show: boolean) => void;
  onClearAll?: () => void;
  showCoreEligibleOnly?: boolean;
  setShowCoreEligibleOnly?: (value: boolean) => void;
  isLoggedIn?: boolean;
  deliveryModelOptions?: { label: string; value: string }[];
  selectedDeliveryModels?: string[];
  setSelectedDeliveryModels?: (models: string[]) => void;
  showAdditionalPurchaseOnly?: boolean;
  setShowAdditionalPurchaseOnly?: (value: boolean) => void;
}


/**
 * ResultsHeader component
 * Displays the results header, applied search, filter chips, and clear/back controls.
 *
 * @param {ResultsHeaderProps} props - Component props
 * @returns {JSX.Element | null}
 */
const ResultsHeader: React.FC<ResultsHeaderProps> = ({
  appliedSearch,
  filteredSolutionsCount,
  selectedCategories = [],
  setSelectedCategories = () => {},
  onClearSearch,
  showCreatedByFilter = false,
  selectedAuthors = [],
  setSelectedAuthors = () => {},
  selectedTypes = [],
  setSelectedTypes = () => {},
  selectedIntegratedSolutions = [],
  setSelectedIntegratedSolutions = () => {},
  showOnlyEntitledSolutions = false,
  setShowOnlyEntitledSolutions = () => {},
  onClearAll,
  showCoreEligibleOnly = false,
  setShowCoreEligibleOnly = () => {},
  isLoggedIn = false,
  deliveryModelOptions = [],
  selectedDeliveryModels = [],
  setSelectedDeliveryModels = () => {},
  showAdditionalPurchaseOnly = false,
  setShowAdditionalPurchaseOnly = () => {},
}) => {
  // Mobile state detection for responsive behavior
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkViewport = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);
  
  // Whether any filters are applied
  const hasFilters = selectedCategories.length > 0 || selectedAuthors.length > 0 || selectedTypes.length > 0 || selectedIntegratedSolutions.length > 0 || showOnlyEntitledSolutions || showCoreEligibleOnly || selectedDeliveryModels.length > 0 || showAdditionalPurchaseOnly;
  // Whether to show the results header (if search or filters are active)
  const showResultsHeader = appliedSearch || hasFilters;

  /**
   * Clears all filters and calls onClearAll callback if provided.
   */
  const handleClearAll = () => {
    setSelectedCategories([]);
    setSelectedAuthors([]);
    setSelectedTypes([]);
    setSelectedIntegratedSolutions([]);
    setShowOnlyEntitledSolutions(false);
    setShowCoreEligibleOnly?.(false);
    setSelectedDeliveryModels([]);
    setShowAdditionalPurchaseOnly(false);
    if (onClearAll) {
      onClearAll();
    }
  };

  /**
   * Handles back arrow click: clears search and filters, calls callbacks.
   */
  const handleBackClick = () => {
    if (appliedSearch) {
      // Clear search and all filters when there's an applied search
      setSelectedCategories([]);
      setSelectedAuthors([]);
      setSelectedTypes([]);
      setSelectedIntegratedSolutions([]);
      setShowOnlyEntitledSolutions(false);
      setShowCoreEligibleOnly?.(false);
      setSelectedDeliveryModels([]);
      setShowAdditionalPurchaseOnly(false);
      if (onClearSearch) {
        onClearSearch();
      }
      if (onClearAll) {
        onClearAll();
      }
    } else if (hasFilters) {
      // Only clear filters if no search is applied
      setSelectedCategories([]);
      setSelectedAuthors([]);
      setSelectedTypes([]);
      setSelectedIntegratedSolutions([]);
      setShowOnlyEntitledSolutions(false);
      setShowCoreEligibleOnly?.(false);
      setSelectedDeliveryModels([]);
      setShowAdditionalPurchaseOnly(false);
      if (onClearAll) {
        onClearAll();
      }
    }
  };

  // Hide header if no search or filters
  if (!showResultsHeader) {
    return null;
  }

  // --- Main render: results header, filter chips, and clear/back controls ---
  return (
    <>
      <style>{`
        .filter-chips-scrollable::-webkit-scrollbar {
          height: 8px;
        }
        .filter-chips-scrollable::-webkit-scrollbar-track {
          background: #e5e5e5;
          border-radius: 4px;
          border: 1px solid #d1d5db;
        }
        .filter-chips-scrollable::-webkit-scrollbar-thumb {
          background: #9ca3af;
          border-radius: 4px;
          border: 1px solid #6b7280;
        }
        .filter-chips-scrollable::-webkit-scrollbar-thumb:hover {
          background: #6b7280;
        }
        .filter-chips-scrollable {
          scrollbar-width: auto;
          scrollbar-color: #9ca3af #e5e5e5;
        }
        /* Mobile-specific styling - force visibility */
        @media (max-width: 767px) {
          .filter-chips-scrollable {
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch !important;
            padding-bottom: 12px !important;
          }
          .filter-chips-scrollable::-webkit-scrollbar {
            height: 14px !important;
            -webkit-appearance: none !important;
            display: block !important;
          }
          .filter-chips-scrollable::-webkit-scrollbar-track {
            background: #d1d5db !important;
            border-radius: 7px !important;
            border: 2px solid #9ca3af !important;
            display: block !important;
          }
          .filter-chips-scrollable::-webkit-scrollbar-thumb {
            background: #374151 !important;
            border-radius: 7px !important;
            border: 1px solid #1f2937 !important;
            display: block !important;
            min-width: 20px !important;
          }
          .filter-chips-scrollable::-webkit-scrollbar-thumb:hover {
            background: #1f2937 !important;
          }
        }
        @media (min-width: 768px) {
          .filter-chips-scrollable {
            overflow-x: visible;
            padding-bottom: 0;
          }
        }
      `}</style>
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      marginBottom: '24px',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Results Header (with back arrow, title, badge) */}
      {(appliedSearch || hasFilters) && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          height: '36px'
        }}>
          {/* Back arrow: clears search/filters and navigates back */}
          <div
            style={{
              width: '16px',
              height: '16px',
              cursor: 'pointer',
              color: '#151618',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={handleBackClick}
          >
            <Icon
              name="arrow-left"
              style={{
                width: '100%',
                height: '100%',
                color: 'inherit'
              }}
            />
          </div>
          {/* Title and badge */}
          <Typography
            style={{
              fontFamily: 'Poppins, Futura, "Century Gothic"',
              fontWeight: 700,
              fontSize: '24px',
              lineHeight: '36px',
              color: '#151618',
              margin: 0,
            }}
          >
            {appliedSearch ? `Results for "${appliedSearch}"` : 'Results'}
          </Typography>
          <Badge
            badgeContent={
              <span style={{ fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto' }}>
                {filteredSolutionsCount}
              </span>
            }
            color="primary"
          />
        </div>
      )}

      {/* Applied Filters Chips */}
      {hasFilters && (
        <div 
          style={{
            marginTop: '8px',
            marginLeft: '28px',
            overflowX: 'auto',
            paddingBottom: '8px'
          }}
          className="filter-chips-scrollable"
        >
          <div style={{
            display: 'flex',
            flexWrap: isMobile ? 'nowrap' : 'wrap',
            gap: '12px',
            alignItems: 'center',
            minWidth: 'fit-content'
          }}>
          {/* Category chips */}
          {selectedCategories.map((category) => (
            <Chip
              key={`category-${category}`}
              label={category}
              onDelete={() => {
                setSelectedCategories(selectedCategories.filter(c => c !== category));
              }}
              style={{
                border: '1px solid #B2B3B7',
                borderRadius: '20px',
                backgroundColor: '#FFF',
                color: '#151618',
                fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                fontSize: '14px',
                fontWeight: 400,
                height: '32px',
              }}
            />
          ))}
          {/* Author/Created By chips */}
          {showCreatedByFilter && selectedAuthors.map((author) => (
            <Chip
              key={`author-${author}`}
              label={author}
              onDelete={() => {
                setSelectedAuthors(selectedAuthors.filter(a => a !== author));
              }}
              style={{
                border: '1px solid #B2B3B7',
                borderRadius: '20px',
                backgroundColor: '#FFF',
                color: '#151618',
                fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                fontSize: '14px',
                fontWeight: 400,
                height: '32px',
              }}
            />
          ))}
          {/* Type chips */}
          {selectedTypes.map((type) => (
            <Chip
              key={`type-${type}`}
              label={type}
              onDelete={() => {
                setSelectedTypes(selectedTypes.filter(t => t !== type));
              }}
              style={{
                border: '1px solid #B2B3B7',
                borderRadius: '20px',
                backgroundColor: '#FFF',
                color: '#151618',
                fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                fontSize: '14px',
                fontWeight: 400,
                height: '32px',
              }}
            />
          ))}
          {/* Integrated Solution chips */}
          {selectedIntegratedSolutions.map((solution) => (
            <Chip
              key={`integrated-${solution}`}
              label={solution}
              onDelete={() => {
                setSelectedIntegratedSolutions(selectedIntegratedSolutions.filter(s => s !== solution));
              }}
              style={{
                border: '1px solid #B2B3B7',
                borderRadius: '20px',
                backgroundColor: '#FFF',
                color: '#151618',
                fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                fontSize: '14px',
                fontWeight: 400,
                height: '32px',
              }}
            />
          ))}
          {/* Entitled chip */}
          {showOnlyEntitledSolutions && (
            <Chip
              key="entitled"
              label="Entitled"
              onDelete={() => {
                setShowOnlyEntitledSolutions(false);
              }}
              style={{
                border: '1px solid #B2B3B7',
                borderRadius: '20px',
                backgroundColor: '#FFF',
                color: '#151618',
                fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                fontSize: '14px',
                fontWeight: 400,
                height: '32px',
              }}
            />
          )}
          {/* Available for core selection chip (visible only when logged in) */}
          {isLoggedIn && showCoreEligibleOnly && (
            <Chip
              key={`core-eligible`}
              label={`Available for core selection`}
              onDelete={() => {
                setShowCoreEligibleOnly(false);
              }}
              style={{
                border: '1px solid #B2B3B7',
                borderRadius: '20px',
                backgroundColor: '#FFF',
                color: '#151618',
                fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                fontSize: '14px',
                fontWeight: 400,
                height: '32px',
              }}
            />
          )}
          {/* Delivery Model chips */}
          {selectedDeliveryModels.map((modelValue) => {
            const option = deliveryModelOptions.find((o) => o.value === modelValue);
            return (
              <Chip
                key={`delivery-model-${modelValue}`}
                label={option ? option.label : modelValue}
                onDelete={() => {
                  setSelectedDeliveryModels(selectedDeliveryModels.filter((m) => m !== modelValue));
                }}
                style={{
                  border: '1px solid #B2B3B7',
                  borderRadius: '20px',
                  backgroundColor: '#FFF',
                  color: '#151618',
                  fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                  fontSize: '14px',
                  fontWeight: 400,
                  height: '32px',
                }}
              />
            );
          })}
          {/* Additional purchase (Cost) chip */}
          {showAdditionalPurchaseOnly && (
            <Chip
              key="additional-purchase"
              label="Additional purchase"
              onDelete={() => setShowAdditionalPurchaseOnly(false)}
              style={{
                border: '1px solid #B2B3B7',
                borderRadius: '20px',
                backgroundColor: '#FFF',
                color: '#151618',
                fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                fontSize: '14px',
                fontWeight: 400,
                height: '32px',
              }}
            />
          )}
          {/* Clear All button */}
          <button
            onClick={handleClearAll}
            style={{
              fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
              fontWeight: 700,
              fontSize: '14px',
              lineHeight: '24px',
              letterSpacing: '0.03em',
              color: '#0056D2',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0',
              textDecoration: 'none',
              outline: 'none',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Clear All
          </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default ResultsHeader;